import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    GridLegacy as Grid,
    Card,
    CardContent,
    TextField,
    useTheme,
    Stack,
    Divider,
    Alert,
    IconButton,
    Snackbar,
    CircularProgress,
    Paper,
    Link,
    Chip,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    FormControlLabel,
    Modal,
    Backdrop,
    Fade,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    Email,
    Phone,
    LocationOn,
    AccessTime,
    SupportAgent,
    ContentCopy,
    CheckCircle,
    WhatsApp,
    LinkedIn,
    Twitter,
    Facebook,
    Send,
    OpenInNew,
    Chat,
    Business,
    Person,
    Close,
    ExpandMore,
    PrivacyTip,
    Flag,
} from '@mui/icons-material';
import { useTranslation } from "react-i18next";
import emailjs from '@emailjs/browser';

interface ContactFormData {
    name: string;
    email: string;
    phone: string;
    phoneCountryCode: string;
    subject: string;
    message: string;
    category: string;
}

interface GDPRConsent {
    accepted: boolean;
    timestamp: Date | null;
}

interface CountryCode {
    code: string;
    name: string;
    pattern: RegExp;
    placeholder: string;
}

const Contact: React.FC = () => {
    const { t } = useTranslation();
    const contactT = (key: string) => t(`contact:${key}`);
    const theme = useTheme();

    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        phone: '',
        phoneCountryCode: '+45',
        subject: '',
        message: '',
        category: 'general',
    });
    const [phoneError, setPhoneError] = useState<string>('');
    const [isValidPhone, setIsValidPhone] = useState<boolean>(true);
    const [formattedPhone, setFormattedPhone] = useState<string>('');

    const [gdprConsent, setGdprConsent] = useState<GDPRConsent>({
        accepted: false,
        timestamp: null,
    });
    const [gdprModalOpen, setGdprModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error' | 'info' | 'warning'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [copied, setCopied] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    // EmailJS Configuration
    const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';
    const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
    const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';

    // Contact information
    const CONTACT_EMAIL = 'info@nordisksupport.com';
    const SUPPORT_EMAIL = 'support@nordisksupport.com';
    const SALES_EMAIL = 'sales@nordisksupport.com';
    const CAREERS_EMAIL = 'careers@nordisksupport.com';
    const PHONE_NUMBER = '+45 66 77 69 51';
    const WHATSAPP_NUMBER = '+4566776951';
    const ADDRESS = 'Copenhagen, Denmark';

    // Country codes with validation patterns
    const countryCodes: CountryCode[] = [
        {
            code: '+45',
            name: 'Denmark',
            pattern: /^(\+45)?\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}$/,
            placeholder: '12 34 56 78'
        },
        {
            code: '+46',
            name: 'Sweden',
            pattern: /^(\+46)?\s?\d{1,3}[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2}$/,
            placeholder: '70 123 45 67'
        },
        {
            code: '+47',
            name: 'Norway',
            pattern: /^(\+47)?\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}$/,
            placeholder: '90 12 34 56'
        },
        {
            code: '+358',
            name: 'Finland',
            pattern: /^(\+358)?\s?\d{1,2}\s?\d{3}\s?\d{3,4}$/,
            placeholder: '40 123 4567'
        },
        {
            code: '+49',
            name: 'Germany',
            pattern: /^(\+49)?\s?\d{3,4}\s?\d{4,12}$/,
            placeholder: '151 23456789'
        },
        {
            code: '+44',
            name: 'UK',
            pattern: /^(\+44)?\s?\d{4}\s?\d{3}\s?\d{3}$/,
            placeholder: '7911 123456'
        },
        {
            code: '+1',
            name: 'USA/Canada',
            pattern: /^(\+1)?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
            placeholder: '(555) 123-4567'
        },
    ];

    // Contact categories - use translation keys
    const contactCategories = [
        { value: 'general', labelKey: 'generalInquiry', email: CONTACT_EMAIL },
        { value: 'support', labelKey: 'technicalSupport', email: SUPPORT_EMAIL },
        { value: 'sales', labelKey: 'salesBusiness', email: SALES_EMAIL },
        { value: 'careers', labelKey: 'careers', email: CAREERS_EMAIL },
    ];

    // Initialize EmailJS
    useEffect(() => {
        if (EMAILJS_PUBLIC_KEY && EMAILJS_PUBLIC_KEY !== 'YOUR_PUBLIC_KEY') {
            emailjs.init(EMAILJS_PUBLIC_KEY);
        }
    }, []);

    // Phone number formatting function
    const formatPhoneNumber = (value: string, countryCode: string = '+45'): string => {
        const cleaned = value.replace(/\D/g, '');
        const country = countryCodes.find(c => c.code === countryCode) || countryCodes[0];

        switch (countryCode) {
            case '+45':
                if (cleaned.length <= 2) return cleaned;
                if (cleaned.length <= 4) return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
                if (cleaned.length <= 6) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4)}`;
                if (cleaned.length <= 8) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6)}`;
                return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)}`;
            case '+46':
                if (cleaned.length <= 2) return cleaned;
                if (cleaned.length <= 3) return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
                if (cleaned.length <= 6) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5)}`;
                if (cleaned.length <= 8) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7)}`;
                return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7, 9)}`;
            case '+47':
                if (cleaned.length <= 2) return cleaned;
                if (cleaned.length <= 4) return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
                if (cleaned.length <= 6) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4)}`;
                if (cleaned.length <= 8) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6)}`;
                return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)}`;
            case '+1':
                if (cleaned.length <= 3) return cleaned;
                if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
                return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
            default:
                if (cleaned.length <= 3) return cleaned;
                if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
                if (cleaned.length <= 9) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
                return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)}`;
        }
    };

    // Phone number validation with translation support
    const validatePhoneNumber = (phone: string, countryCode: string): { isValid: boolean; message: string; formatted: string } => {
        if (!phone) {
            return { isValid: true, message: '', formatted: '' };
        }

        const cleanedPhone = phone.replace(/\D/g, '');
        const country = countryCodes.find(c => c.code === countryCode);

        if (!country) {
            return { isValid: false, message: 'Invalid country code', formatted: phone };
        }

        const fullNumber = countryCode + cleanedPhone;
        const pattern = country.pattern;

        const isValid = pattern.test(countryCode + ' ' + phone) || pattern.test(phone);

        if (!isValid) {
            // Map country codes to translation keys
            const countryErrorKeys: { [key: string]: string } = {
                '+45': 'phoneError.dk',
                '+46': 'phoneError.se',
                '+47': 'phoneError.no',
                '+358': 'phoneError.fi',
                '+49': 'phoneError.de',
                '+44': 'phoneError.uk',
                '+1': 'phoneError.us',
            };

            const errorKey = countryErrorKeys[countryCode];
            let message = errorKey ? contactT(errorKey) : `Please enter a valid ${country.name} phone number`;

            return { isValid: false, message, formatted: phone };
        }

        const formatted = formatPhoneNumber(cleanedPhone, countryCode);
        return { isValid: true, message: '', formatted };
    };

    // Handle phone number change with formatting
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const validation = validatePhoneNumber(value, formData.phoneCountryCode);

        setFormData(prev => ({
            ...prev,
            phone: value
        }));

        setFormattedPhone(validation.formatted);
        setIsValidPhone(validation.isValid);
        setPhoneError(validation.message);
    };

    // Handle country code change
    const handleCountryCodeChange = (code: string) => {
        setFormData(prev => ({
            ...prev,
            phoneCountryCode: code,
            phone: ''
        }));
        setFormattedPhone('');
        setPhoneError('');
        setIsValidPhone(true);
    };

    // Handle GDPR Checkbox Change
    const handleGdprConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isAccepted = event.target.checked;
        setGdprConsent({
            accepted: isAccepted,
            timestamp: isAccepted ? new Date() : null
        });

        if (isAccepted) {
            setSnackbar({
                open: true,
                message: contactT('snackbar.thankYouGdpr'),
                severity: 'success'
            });
        }
    };

    const openGdprModal = () => setGdprModalOpen(true);
    const closeGdprModal = () => setGdprModalOpen(false);

    // Handle form change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCategoryChange = (category: string) => {
        setFormData(prev => ({
            ...prev,
            category
        }));
    };

    const getCurrentEmail = () => {
        const category = contactCategories.find(cat => cat.value === formData.category);
        return category ? category.email : CONTACT_EMAIL;
    };

    const generateEmailContent = () => {
        const { name, email, phone, phoneCountryCode, subject, message, category } = formData;
        const categoryLabel = contactCategories.find(cat => cat.value === category)?.labelKey
            ? contactT(contactCategories.find(cat => cat.value === category)!.labelKey)
            : contactT('generalInquiry');
        const gdprStatus = gdprConsent.accepted ?
            `GDPR Consent: Accepted on ${gdprConsent.timestamp?.toLocaleString('en-GB')}` :
            'GDPR Consent: Not accepted';

        const displayPhone = phone ? `${phoneCountryCode} ${phone}` : contactT('phoneNumber');

        const emailSubject = subject
            ? `${subject} (${categoryLabel})`
            : `${categoryLabel} - ${name || contactT('yourName')}`;

        const emailBody = `Dear Nordisk Support Team,

${message || contactT('yourMessage')}

---
${contactT('contactInfoTitle')}:
${contactT('yourName')}: ${name || contactT('yourName')}
${contactT('emailAddress')}: ${email || contactT('emailAddress')}
${contactT('phoneNumber')}: ${displayPhone}
${contactT('subject')}: ${categoryLabel}
${gdprStatus}
${contactT('openingEmailClient')}: ${new Date().toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })}
---
            
${contactT('gdprComplianceAlert')}
            
Best regards,
${name || contactT('yourName')}
${email ? `${contactT('emailAddress')}: ${email}` : ''}
${phone ? `${contactT('phoneNumber')}: ${displayPhone}` : ''}`.trim();

        return {
            subject: emailSubject,
            body: emailBody,
            toEmail: getCurrentEmail(),
            encodedSubject: encodeURIComponent(emailSubject),
            encodedBody: encodeURIComponent(emailBody),
            mailtoLink: `mailto:${getCurrentEmail()}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`
        };
    };

    // Secure email client opener (fallback only)
    const openEmailClient = () => {
        const { mailtoLink } = generateEmailContent();

        // Create a temporary anchor element
        const link = document.createElement('a');
        link.href = mailtoLink;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return true;
    };

    const isValidEmail = (email: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // Main form submission with EmailJS
    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (!gdprConsent.accepted) {
            setSnackbar({
                open: true,
                message: contactT('snackbar.acceptGdpr'),
                severity: 'error'
            });
            return;
        }

        if (formData.phone && !isValidPhone) {
            setSnackbar({
                open: true,
                message: contactT('snackbar.validPhone'),
                severity: 'error'
            });
            return;
        }

        if (!formData.message.trim()) {
            setSnackbar({
                open: true,
                message: contactT('snackbar.enterMessage'),
                severity: 'error'
            });
            return;
        }

        if (formData.email && !isValidEmail(formData.email)) {
            setSnackbar({
                open: true,
                message: contactT('snackbar.validEmail'),
                severity: 'error'
            });
            return;
        }

        setIsSubmitting(true);

        try {
            // Prepare EmailJS template parameters
            const templateParams = {
                from_name: formData.name || contactT('anonymousUser'),
                from_email: formData.email || 'no-email@nordisksupport.com',
                phone: formData.phone ? `${formData.phoneCountryCode} ${formData.phone}` : contactT('notProvided'),
                subject: formData.subject || `${contactT(formData.category)} Inquiry`,
                message: formData.message,
                category: contactT(formData.category),
                gdpr_consent: gdprConsent.accepted ? contactT('accepted') : contactT('notAccepted'),
                gdpr_timestamp: gdprConsent.timestamp?.toLocaleString('en-GB') || '',
                submission_date: new Date().toLocaleString('en-GB'),
                reply_to: formData.email,
                to_email: getCurrentEmail(),
                user_agent: navigator.userAgent,
                page_url: window.location.href,
                submission_id: `NS-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            };

            // Send email using EmailJS
            const result = await emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                templateParams
            );

            if (result.status === 200 || result.status === 201) {
                // Success
                setSnackbar({
                    open: true,
                    message: contactT('snackbar.messageSent'),
                    severity: 'success'
                });

                // Reset form after successful submission
                setTimeout(() => {
                    setFormData({
                        name: '',
                        email: '',
                        phone: '',
                        phoneCountryCode: '+45',
                        subject: '',
                        message: '',
                        category: 'general',
                    });
                    setGdprConsent({
                        accepted: false,
                        timestamp: null
                    });
                    setFormattedPhone('');
                    setPhoneError('');
                    setIsValidPhone(true);
                    setIsSubmitting(false);
                }, 1500);
            } else {
                throw new Error('Email service returned unexpected status');
            }
        } catch (error) {
            console.error('EmailJS Error:', error);

            // Fallback to email client if EmailJS fails
            setSnackbar({
                open: true,
                message: contactT('snackbar.fallingBackToEmail'),
                severity: 'warning'
            });

            // Try opening email client as fallback
            setTimeout(() => {
                try {
                    const success = openEmailClient();
                    if (success) {
                        setSnackbar({
                            open: true,
                            message: contactT('snackbar.openingEmail'),
                            severity: 'info'
                        });
                    }
                } catch (emailError) {
                    console.error('Email fallback error:', emailError);
                    setSnackbar({
                        open: true,
                        message: contactT('snackbar.sendFailed'),
                        severity: 'error'
                    });
                }
                setIsSubmitting(false);
            }, 2000);
        }
    };

    const copyEmailContent = () => {
        const { subject, body, toEmail } = generateEmailContent();
        const emailContent = `${contactT('to')} ${toEmail}
${contactT('subject')}: ${subject}

${body}`;

        navigator.clipboard.writeText(emailContent)
            .then(() => {
                setCopied(true);
                setSnackbar({
                    open: true,
                    message: contactT('snackbar.copied'),
                    severity: 'success'
                });
                setTimeout(() => setCopied(false), 3000);
            })
            .catch(err => {
                setSnackbar({
                    open: true,
                    message: contactT('snackbar.copyFailed'),
                    severity: 'error'
                });
            });
    };

    const openWhatsApp = () => {
        const message = `Hello Nordisk Support,\n\nI would like to get in touch regarding: ${formData.category}\n\n${contactT('yourName')}: ${formData.name || contactT('yourName')}\n${contactT('emailAddress')}: ${formData.email || contactT('emailAddress')}\n${contactT('phoneNumber')}: ${formData.phoneCountryCode} ${formData.phone || contactT('phoneNumber')}\nGDPR Consent: ${gdprConsent.accepted ? 'Accepted' : contactT('gdprRequired')}`;
        const encodedMessage = encodeURIComponent(message);
        const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER.replace(/\D/g, '')}?text=${encodedMessage}`;
        window.open(whatsappLink, '_blank', 'noopener,noreferrer');
    };

    const getPhonePlaceholder = () => {
        const country = countryCodes.find(c => c.code === formData.phoneCountryCode);
        return country ? country.placeholder : '12 34 56 78';
    };

    // GDPR Modal Styles
    const gdprModalStyle = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: 900,
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const,
    };

    const contentStyle = {
        overflowY: 'auto' as const,
        maxHeight: 'calc(90vh - 140px)',
        p: 3,
    };

    // Auto-format phone number on country code change
    useEffect(() => {
        if (formData.phone) {
            const validation = validatePhoneNumber(formData.phone, formData.phoneCountryCode);
            setFormattedPhone(validation.formatted);
            setIsValidPhone(validation.isValid);
            setPhoneError(validation.message);
        }
    }, [formData.phoneCountryCode]);

    return (
        <Box sx={{ overflow: 'hidden', background: "transparent", pl: 0 }}>
            {/* Hero Section */}
            <Box
                sx={{
                    flex: 1,
                    m: 0,
                    p: 0,
                    px: 0,
                    py: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        m: 0,
                        backdropFilter: "blur(10px)",
                        background: "linear-gradient(90deg, transparent 0%, cadetblue 100%)",
                        height: { xs: 'auto', md: '350px' },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h2" sx={{ textAlign: "center" }} >
                        {contactT('contactHeroTitle')}
                    </Typography>

                    <Typography
                        variant="h6"
                        sx={{
                            textAlign: "center",
                            justifyContent: "center",
                            opacity: 0.7,
                            lineHeight: 1.6,
                            pb: 4
                        }}
                    >
                        {contactT('contactHeroSubtitle')}
                    </Typography>

                    <Box
                        sx={{
                            display: "inline-flex",
                            gap: 2,
                            justifyContent: "center",
                            flexWrap: "wrap",
                            flexDirection: "row",
                            alignItems: "center",
                        }}
                    >
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Phone />}
                            sx={{
                                backgroundColor: 'white',
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                px: 4,
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: '#f8f9fa',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                                },
                                transition: 'all 0.3s ease'
                            }}
                            href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}
                        >
                            {PHONE_NUMBER}
                        </Button>

                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<WhatsApp />}
                            sx={{
                                borderColor: 'white',
                                color: 'white',
                                fontWeight: 600,
                                px: 4,
                                py: 1.5,
                                '&:hover': {
                                    borderColor: '#25D366',
                                    backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                    transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.3s ease'
                            }}
                            onClick={openWhatsApp}
                        >
                            {contactT('whatsappButton')}
                        </Button>
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                        display: { xs: "none", md: "flex" }
                    }}
                >
                    <img
                        src="/logos/contact.webp"
                        alt="IT Support Illustration"
                        style={{ width: "100%", height: "350px", objectFit: "cover" }}
                    />
                </Box>
            </Box>

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Grid container spacing={6}>
                    {/* Left Column: Contact Info */}
                    <Grid item xs={12} md={4}>
                        <Stack spacing={4}>
                            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                                <Typography variant="h5" textAlign={'center'} >
                                    {contactT('contactInfoTitle')}
                                </Typography>
                                <List>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: '36px' }}>
                                            <AccessTime color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            secondary={<>
                                                {contactT('weekdaysHours')}
                                                <br />
                                                {contactT('weekendHours')}
                                            </>}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: '36px' }}>
                                            <Phone color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            secondary={
                                                <Link
                                                    href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}
                                                    sx={{
                                                        whiteSpace: 'nowrap',
                                                        display: 'inline-block',
                                                    }}
                                                >
                                                    {PHONE_NUMBER}
                                                </Link>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: '36px' }}>
                                            <LocationOn color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            secondary={contactT('address')}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: '36px' }}>
                                            <SupportAgent color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            secondary={contactT('support24_7')}
                                        />
                                    </ListItem>
                                </List>
                            </Paper>

                            <Paper elevation={1} >
                                <Box
                                    sx={{
                                        flex: 1,
                                        alignItems: "center",
                                        justifyContent: "center",
                                        display: { xs: "none", md: "flex" }
                                    }}
                                >
                                    <img
                                        src="/logos/chatmodel.png"
                                        alt="IT Support Illustration"
                                        style={{ width: "100%", height: "500px", objectFit: "cover" }}
                                    />
                                </Box>
                            </Paper>
                        </Stack>
                    </Grid>

                    {/* Right Column: Contact Form */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3 }}>
                            <Typography variant="h4" gutterBottom fontWeight={700}>
                                {contactT('sendMessageTitle')}
                            </Typography>

                            <form onSubmit={handleEmailSubmit}>
                                <Stack spacing={2}>
                                    {/* Category Selection */}
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {contactT('selectTeam')}
                                        </Typography>
                                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} flexWrap="wrap">
                                            {contactCategories.map((category) => (
                                                <Chip
                                                    key={category.value}
                                                    label={contactT(category.labelKey)}
                                                    onClick={() => handleCategoryChange(category.value)}
                                                    color={formData.category === category.value ? "primary" : "default"}
                                                    variant={formData.category === category.value ? "filled" : "outlined"}
                                                    sx={{ mb: 1 }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>

                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={6} >
                                            <TextField
                                                fullWidth
                                                label={contactT('yourName')}
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder={contactT('yourName')}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label={contactT('emailAddress')}
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="you@example.com"
                                                variant="outlined"
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Phone Number Field with Country Code Selector */}
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth variant="outlined">
                                                <Select
                                                    sx={{
                                                        height: 60,
                                                        '& .MuiSelect-select': {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '16.5px 14px',
                                                        },
                                                    }}
                                                    value={formData.phoneCountryCode}
                                                    onChange={(e) => handleCountryCodeChange(e.target.value)}
                                                >
                                                    {countryCodes.map((country) => (
                                                        <MenuItem key={country.code} value={country.code}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Typography variant="body2">{country.code}</Typography>
                                                                <Typography>{country.name}</Typography>
                                                            </Stack>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={8}>
                                            <TextField
                                                fullWidth
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handlePhoneChange}
                                                placeholder={getPhonePlaceholder()}
                                                variant="outlined"
                                                error={!isValidPhone && formData.phone.length > 0}
                                                helperText={phoneError}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Phone color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <TextField
                                        fullWidth
                                        label={contactT('subject')}
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        placeholder="How can we help you?"
                                        variant="outlined"
                                    />

                                    <Box>
                                        <TextField
                                            fullWidth
                                            label={contactT('yourMessage')}
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            multiline
                                            rows={1}
                                            variant="outlined"
                                            placeholder="Please describe your inquiry in detail..."
                                            required
                                            InputProps={{
                                                sx: {
                                                    '& textarea': {
                                                        resize: 'vertical',
                                                        minHeight: '150px'
                                                    }
                                                }
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            {formData.message.length} {contactT('characters')}
                                        </Typography>
                                    </Box>

                                    {/* GDPR Agreement Section */}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            bgcolor: gdprConsent.accepted ? 'success.light' : 'background.default',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={gdprConsent.accepted}
                                                        onChange={handleGdprConsentChange}
                                                        color="primary"
                                                        icon={<PrivacyTip />}
                                                        checkedIcon={<CheckCircle />}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2">
                                                        {contactT('gdprAgreement')}
                                                    </Typography>
                                                }
                                            />
                                            <Button
                                                variant="text"
                                                size="small"
                                                startIcon={<OpenInNew />}
                                                onClick={openGdprModal}
                                                sx={{ ml: 'auto' }}
                                            >
                                                {contactT('readAgreement')}
                                            </Button>
                                        </Stack>

                                        {gdprConsent.accepted && gdprConsent.timestamp && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                Consent given: {gdprConsent.timestamp.toLocaleString('en-GB')}
                                            </Typography>
                                        )}
                                    </Paper>

                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        flexWrap: 'wrap',
                                        pt: 2
                                    }}>
                                        <Tooltip
                                            title={!gdprConsent.accepted ? contactT('snackbar.acceptGdpr') : ""}
                                            placement="top"
                                        >
                                            <span style={{ display: 'inline-flex' }}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    size="large"
                                                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                                    disabled={isSubmitting || !formData.message.trim() || !gdprConsent.accepted}
                                                    sx={{
                                                        px: 4,
                                                        py: 1.5,
                                                        fontWeight: 600,
                                                        fontSize: '1.1rem',
                                                        minWidth: 200,
                                                        opacity: gdprConsent.accepted ? 1 : 0.7
                                                    }}
                                                >
                                                    {isSubmitting ? contactT('sending') : contactT('sendMessage')}
                                                </Button>
                                            </span>
                                        </Tooltip>

                                        <Button
                                            variant="outlined"
                                            size="large"
                                            startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                                            onClick={copyEmailContent}
                                        >
                                            {copied ? contactT('copied') : contactT('copyContent')}
                                        </Button>

                                        <Button
                                            variant="text"
                                            size="large"
                                            onClick={() => setPreviewOpen(true)}
                                        >
                                            {contactT('previewEmail')}
                                        </Button>

                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                            {gdprConsent.accepted ? contactT('gdprAccepted') : contactT('gdprRequired')}
                                        </Typography>
                                    </Box>

                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            <strong>GDPR Compliance:</strong> {contactT('gdprComplianceAlert')}
                                        </Typography>
                                    </Alert>

                                    {/* EmailJS Status (for debugging) */}
                                    {process.env.NODE_ENV === 'development' && (
                                        <Alert severity="info" sx={{ mt: 1 }}>
                                            <Typography variant="caption">
                                                Using EmailJS: {EMAILJS_SERVICE_ID !== 'YOUR_SERVICE_ID' ? 'Configured' : 'Not configured'}
                                            </Typography>
                                        </Alert>
                                    )}
                                </Stack>
                            </form>

                            {/* Alternative Contact Methods */}
                            <Box sx={{ mt: 6, pt: 4, borderTop: 1, borderColor: 'divider' }}>
                                <Typography variant="h6" gutterBottom>
                                    {contactT('alternativeContactMethods')}
                                </Typography>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
                                    <Button
                                        startIcon={<WhatsApp />}
                                        variant="outlined"
                                        onClick={openWhatsApp}
                                        sx={{
                                            borderColor: '#25D366',
                                            color: '#25D366',
                                            '&:hover': {
                                                borderColor: '#128C7E',
                                                backgroundColor: 'rgba(37, 211, 102, 0.1)'
                                            }
                                        }}
                                    >
                                        {contactT('whatsapp')}
                                    </Button>
                                    <Button
                                        startIcon={<LinkedIn />}
                                        variant="outlined"
                                        href="https://www.linkedin.com/company/nordisksupport/?viewAsMember=true"
                                        target="_blank"
                                    >
                                        {contactT('linkedin')}
                                    </Button>
                                    <Button
                                        startIcon={<SupportAgent />}
                                        variant="outlined"
                                        href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}
                                    >
                                        {contactT('callNow')}
                                    </Button>
                                </Stack>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* GDPR Agreement Modal */}
            <Modal
                open={gdprModalOpen}
                onClose={closeGdprModal}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={gdprModalOpen}>
                    <Box sx={gdprModalStyle}>
                        <DialogTitle sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Typography variant="h5">
                                {contactT('gdprModalTitle')}
                            </Typography>
                            <IconButton onClick={closeGdprModal} sx={{ color: 'inherit' }}>
                                <Close />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent dividers sx={contentStyle}>
                            <Box sx={{ mb: 3 }}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Important:</strong> {contactT('gdprImportantNote')}
                                    </Typography>
                                </Alert>

                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="h6">{contactT('gdprComplianceAgreement')}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                            {contactT('gdprContent')}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>

                                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {contactT('whatHappensWhenYouAgree')}
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <CheckCircle color="success" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={contactT('consentLogged')}
                                                secondary="We keep a record of when you accepted our terms"
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <CheckCircle color="success" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={contactT('emailSubmissionEnabled')}
                                                secondary="The 'Send Message' button becomes active"
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <CheckCircle color="success" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={contactT('yourRightsProtected')}
                                                secondary="You maintain all GDPR rights mentioned above"
                                            />
                                        </ListItem>
                                    </List>
                                </Box>
                            </Box>
                        </DialogContent>

                        <DialogActions sx={{ p: 2, bgcolor: 'grey.100' }}>
                            <Button onClick={closeGdprModal}>
                                {contactT('close')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    handleGdprConsentChange({ target: { checked: true } } as React.ChangeEvent<HTMLInputElement>);
                                    closeGdprModal();
                                }}
                                startIcon={<CheckCircle />}
                            >
                                {contactT('acceptAndAgree')}
                            </Button>
                        </DialogActions>
                    </Box>
                </Fade>
            </Modal>

            {/* Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">{contactT('emailPreview')}</Typography>
                        <IconButton onClick={() => setPreviewOpen(false)} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {(() => {
                        const { subject, body, toEmail } = generateEmailContent();
                        return (
                            <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">{contactT('to')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{toEmail}</Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">{contactT('subject')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{subject}</Typography>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    {body}
                                </Box>
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>
                        {contactT('close')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setPreviewOpen(false);
                            handleEmailSubmit({ preventDefault: () => { } } as React.FormEvent);
                        }}
                        startIcon={<Send />}
                    >
                        {contactT('sendMessage')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Contact;